import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Eye, EyeOff, ArrowRight, CheckCircle, Loader2,
  TrendingUp, Activity, Shield, Smartphone, ArrowLeft, Mail
} from 'lucide-react';
import axios from 'axios';
import Logo from '../components/Logo';
import { trackLead, trackCompleteRegistration, trackInitiateCheckout } from '../utils/fbPixel';

// Steps: 1 = form, 2 = redirecting to Stripe (OTP removed)
const STEPS = ['Your Info', 'Payment'];

const ValuePoint = ({ icon: Icon, title, sub }) => (
  <div className="flex items-start gap-3">
    <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
      style={{ background: 'rgba(96,165,250,0.15)', border: '1px solid rgba(96,165,250,0.2)' }}>
      <Icon size={14} style={{ color: '#93C5FD' }} />
    </div>
    <div>
      <p className="text-sm font-semibold text-white leading-tight">{title}</p>
      <p className="text-xs mt-0.5" style={{ color: 'rgba(148,163,184,0.8)' }}>{sub}</p>
    </div>
  </div>
);

export default function RegisterPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [otpSessionId, setOtpSessionId] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpResending, setOtpResending] = useState(false);
  const [otpDelivery, setOtpDelivery] = useState('sms');
  const [verifyMethod, setVerifyMethod] = useState('');
  const [showMethodPicker, setShowMethodPicker] = useState(false);

  const COUNTRY_CODES = [
    { code: '+1',  flag: '🇺🇸', label: 'US', name: 'United States' },
    { code: '+1',  flag: '🇨🇦', label: 'CA', name: 'Canada' },
    { code: '+44', flag: '🇬🇧', label: 'UK', name: 'United Kingdom' },
    { code: '+61', flag: '🇦🇺', label: 'AU', name: 'Australia' },
    { code: '+52', flag: '🇲🇽', label: 'MX', name: 'Mexico' },
  ];

  const [countryCode, setCountryCode] = useState('+1');
  const [countryFlag, setCountryFlag] = useState('🇺🇸');
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [form, setForm] = useState({
    org_name: '', full_name: '', username: '', phone: '', password: '', confirm_password: '',
    referral_source: '', fabg_member: false
  });
  const [errors, setErrors] = useState({});
  const [showFabgQuestion, setShowFabgQuestion] = useState(false);

  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.org_name.trim()) e.org_name = 'Organization name required';
    if (!form.full_name.trim()) e.full_name = 'Your name required';
    if (!form.username.trim()) e.username = 'Email required';
    if (!form.phone.trim()) e.phone = null; // phone is optional
    else if (form.phone.replace(/[^0-9]/g,'').length < 10) e.phone = 'Enter a valid 10+ digit phone number';
    if (form.password.length < 6) e.password = 'Password must be at least 6 characters';
    if (form.password !== form.confirm_password) e.confirm_password = 'Passwords do not match';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Step 1 → send OTP, go to step 2
  // Helper: register + redirect to Stripe (OTP removed)
  const registerAndCheckout = async (sessionId) => {
    // Save password temporarily for auto-login after payment
    sessionStorage.setItem('reg_password', form.password);
    
    const res = await axios.post(`${API}/public/register`, {
      org_name: form.org_name.trim(),
      full_name: form.full_name.trim(),
      username: form.username.trim().toLowerCase(),
      password: form.password,
      phone: form.phone.trim() || null,
      otp_session_id: null,  // No OTP needed
      referral_source: form.referral_source || null,
      fabg_member: form.fabg_member || false,
    }, { headers: { origin: window.location.origin } });
    
    setStep(2);  // Go straight to payment redirect
    trackCompleteRegistration({ value: 249, currency: 'USD' });
    trackInitiateCheckout();
    setTimeout(() => { window.location.href = res.data.checkout_url; }, 1200);
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      // Skip OTP verification - go straight to payment
      trackLead({ content_name: form.org_name });
      await registerAndCheckout(null);  // No OTP session needed
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed. Please try again.');
    } finally { setLoading(false); }
  };

  // Step 2 → verify OTP then register + pay
  const verifyCode = async (codeToVerify) => {
    const code = (codeToVerify || otpCode).replace(/\D/g, '');
    if (code.length !== 6) { setOtpError('Please enter the full 6-digit code'); return; }
    setOtpError('');
    setLoading(true);
    try {
      await axios.post(`${API}/public/verify-otp`, { session_id: otpSessionId, code });
      await registerAndCheckout(otpSessionId);
    } catch (err) {
      setOtpError(err?.response?.data?.detail || 'Verification failed. Check the code and try again.');
    } finally { setLoading(false); }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    await verifyCode(otpCode);
  };

  const resendOtp = async () => {
    setOtpResending(true);
    setOtpCode('');
    setOtpError('');
    try {
      const method = verifyMethod || otpDelivery || 'sms';
      const res = await axios.post(`${API}/public/send-otp`, {
        phone: form.phone || '',
        email: form.username.trim().toLowerCase(),
        country_code: countryCode,
        method: method,
      });
      setOtpSessionId(res.data.session_id);
      const via = method === 'sms' ? 'SMS' : 'email';
      toast.success(`New code sent via ${via}!`);
    } catch { toast.error('Failed to resend'); }
    finally { setOtpResending(false); }
  };

  const inputStyle = { background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.12)', color: 'white' };

  return (
    <div className="min-h-screen flex" style={{ background: '#0A0F1E' }}>

      {/* Left — brand panel */}
      <div className="hidden lg:flex flex-col flex-1 relative overflow-hidden px-14 py-12">
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(900px circle at 15% 10%, rgba(30,64,175,0.22), rgba(10,15,30,0) 60%)'
        }} />
        <div className="relative z-10 flex flex-col h-full">
          <Logo size={40} showText light />
          <div className="flex-1 flex flex-col justify-center max-w-md">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6"
              style={{ background: 'rgba(37,99,235,0.15)', border: '1px solid rgba(37,99,235,0.3)' }}>
              <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
              <span className="text-xs font-semibold" style={{ color: '#93C5FD' }}>Starting at $249/month</span>
            </div>
            <h1 className="font-heading text-4xl font-bold text-white leading-tight mb-3"
              style={{ letterSpacing: '-0.02em' }}>
              Your practice's<br />
              <span style={{ color: '#60A5FA' }}>performance hub.</span>
            </h1>
            <p className="text-base mb-10 leading-relaxed" style={{ color: 'rgba(148,163,184,0.85)' }}>
              Multi-location Full Arch analytics — consult funnel, close rates, marketing ROI, and revenue all in one dashboard.
            </p>
            <div className="space-y-4">
              <ValuePoint icon={TrendingUp} title="Real-time close rate tracking" sub="Same-day, normalized, 30-day & 90-day rolling rates" />
              <ValuePoint icon={Activity} title="Full Arch marketing ROI" sub="Cost per lead, booking, arch by referral source" />
              <ValuePoint icon={Shield} title="Multi-location · Role-based access" sub="Per-location goals, staff forms, audit log" />
            </div>
          </div>
          <div className="flex items-center gap-6 pt-8 border-t" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
            {['$249/mo base','First location included','+$29/mo per extra location'].map(t => (
              <div key={t} className="flex items-center gap-1.5">
                <CheckCircle size={12} style={{ color: '#60A5FA' }} />
                <span className="text-xs font-medium" style={{ color: 'rgba(148,163,184,0.6)' }}>{t}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="hidden lg:block w-px" style={{ background: 'rgba(255,255,255,0.07)' }} />

      {/* Right — form panel */}
      <div className="flex-1 lg:max-w-[480px] flex flex-col justify-center px-8 py-12"
        style={{ background: '#0D1526' }}>
        <div className="w-full max-w-sm mx-auto">
          <div className="lg:hidden mb-8"><Logo size={36} showText light /></div>

          {/* Step indicator */}
          <div className="flex items-center gap-2 mb-8">
            {STEPS.map((s, i) => (
              <React.Fragment key={s}>
                <div className="flex items-center gap-1.5">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                    step > i + 1 ? 'bg-green-500 text-white'
                    : step === i + 1 ? 'bg-blue-600 text-white'
                    : 'border border-white/20 text-white/30'
                  }`}>
                    {step > i + 1 ? '✓' : i + 1}
                  </div>
                  <span className={`text-xs font-medium ${
                    step === i + 1 ? 'text-white' : 'text-slate-600'
                  }`}>{s}</span>
                </div>
                {i < STEPS.length - 1 && <div className="flex-1 h-px bg-white/10" />}
              </React.Fragment>
            ))}
          </div>

          {/* ── STEP 1: Form ── */}
          {step === 1 && (
            <>
              <h2 className="font-heading text-2xl font-bold text-white mb-1" style={{ letterSpacing: '-0.02em' }}>Create account</h2>
              <p className="text-sm mb-7" style={{ color: 'rgba(148,163,184,0.7)' }}>Set up your practice in 60 seconds</p>
              <form onSubmit={handleSendOtp} className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Organization Name *</Label>
                  <Input value={form.org_name} onChange={e => setField('org_name', e.target.value)}
                    placeholder="Bright Smile Dental Group" style={inputStyle}
                    className="h-11" />
                  {errors.org_name && <p className="text-xs text-red-400">{errors.org_name}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Your Full Name *</Label>
                  <Input value={form.full_name} onChange={e => setField('full_name', e.target.value)}
                    placeholder="Dr. Jane Smith" style={inputStyle} className="h-11" />
                  {errors.full_name && <p className="text-xs text-red-400">{errors.full_name}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Email *</Label>
                  <Input value={form.username} onChange={e => setField('username', e.target.value)}
                    placeholder="you@practice.com" type="email" style={inputStyle} className="h-11" />
                  {errors.username && <p className="text-xs text-red-400">{errors.username}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">How did you hear about us?</Label>
                  <select
                    value={form.referral_source}
                    onChange={e => {
                      setField('referral_source', e.target.value);
                      setShowFabgQuestion(e.target.value === 'Full Arch Buying Group');
                      if (e.target.value !== 'Full Arch Buying Group') setField('fabg_member', false);
                    }}
                    style={{
                      background: 'rgba(255,255,255,0.06)',
                      borderColor: 'rgba(255,255,255,0.12)',
                      color: 'white'
                    }}
                    className="h-11 w-full rounded-xl px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 [&>option]:bg-slate-900 [&>option]:text-white"
                  >
                    <option value="" className="bg-slate-900 text-slate-400">Select a source...</option>
                    <option value="Google Search" className="bg-slate-900 text-white">Google Search</option>
                    <option value="Social Media" className="bg-slate-900 text-white">Social Media</option>
                    <option value="Referral" className="bg-slate-900 text-white">Referral</option>
                    <option value="Full Arch Buying Group" className="bg-slate-900 text-white">Full Arch Buying Group</option>
                    <option value="Conference/Event" className="bg-slate-900 text-white">Conference/Event</option>
                    <option value="Other" className="bg-slate-900 text-white">Other</option>
                  </select>
                </div>
                {showFabgQuestion && (
                  <div className="space-y-1.5 p-3 rounded-xl" style={{ background: 'rgba(37,99,235,0.08)', border: '1px solid rgba(37,99,235,0.2)' }}>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.fabg_member}
                        onChange={e => setField('fabg_member', e.target.checked)}
                        className="w-4 h-4 rounded border-2 border-blue-500 bg-transparent checked:bg-blue-600 cursor-pointer"
                      />
                      <span className="text-sm font-medium text-white">I am a current Full Arch Buying Group member</span>
                    </label>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400 flex items-center gap-1">
                    <Smartphone size={11} className="text-blue-400" /> Cell Phone
                  </Label>
                  <div className="flex gap-2">
                    {/* Country code picker */}
                    <div className="relative flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => setShowCountryPicker(!showCountryPicker)}
                        className="h-11 px-3 rounded-xl flex items-center gap-1.5 text-sm font-semibold transition-colors"
                        style={{ background: 'rgba(255,255,255,0.06)', border: '2px solid rgba(255,255,255,0.12)', color: 'white', minWidth: 80 }}
                      >
                        <span>{countryFlag}</span>
                        <span style={{ color: 'rgba(148,163,184,0.8)' }}>{countryCode}</span>
                        <span style={{ color: 'rgba(100,116,139,0.6)', fontSize: 10 }}>▾</span>
                      </button>
                      {showCountryPicker && (
                        <div className="absolute top-12 left-0 z-50 rounded-xl overflow-hidden shadow-2xl"
                          style={{ background: '#1E293B', border: '1px solid rgba(255,255,255,0.1)', minWidth: 200 }}>
                          {COUNTRY_CODES.map((c, i) => (
                            <button key={`${c.code}-${c.label}`} type="button"
                              onClick={() => { setCountryCode(c.code); setCountryFlag(c.flag); setShowCountryPicker(false); }}
                              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left hover:bg-white/10 transition-colors"
                              style={{ color: 'rgba(226,232,240,0.9)' }}>
                              <span className="text-lg">{c.flag}</span>
                              <span className="flex-1">{c.name}</span>
                              <span style={{ color: 'rgba(100,116,139,0.7)' }}>{c.code}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Phone number input */}
                    <Input value={form.phone} onChange={e => setField('phone', e.target.value.replace(/[^0-9\-\(\)\s]/g, ''))}
                      placeholder="(555) 000-0000" type="tel" style={inputStyle} className="h-11 flex-1" />
                  </div>
                  {errors.phone
                    ? <p className="text-xs text-red-400">{errors.phone}</p>
                    : <p className="text-xs" style={{ color: 'rgba(100,116,139,0.5)' }}>
                        Code sent via SMS + email backup to {form.username || 'your email'}
                      </p>
                  }
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Password *</Label>
                    <div className="relative">
                      <Input type={showPw ? 'text' : 'password'} value={form.password}
                        onChange={e => setField('password', e.target.value)}
                        placeholder="Min 6 chars" style={inputStyle} className="h-11 pr-9" />
                      <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
                        onClick={() => setShowPw(!showPw)}>
                        {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                    {errors.password && <p className="text-xs text-red-400">{errors.password}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Confirm *</Label>
                    <Input type="password" value={form.confirm_password}
                      onChange={e => setField('confirm_password', e.target.value)}
                      placeholder="Repeat" style={inputStyle} className="h-11" />
                    {errors.confirm_password && <p className="text-xs text-red-400">{errors.confirm_password}</p>}
                  </div>
                </div>
                <Button type="submit" className="w-full h-11 gap-2 font-semibold mt-2" disabled={loading}>
                  {loading ? <><Loader2 size={15} className="animate-spin" /> Please wait…</>
                    : <><ArrowRight size={15} /> Continue to Payment</>}
                </Button>
              </form>

              {/* ── Method Picker Modal ── */}
              {showMethodPicker && (
                <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
                  style={{ background: 'rgba(0,0,0,0.7)' }}>
                  <div className="w-full max-w-sm rounded-2xl overflow-hidden"
                    style={{ background: '#0D1526', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 12px 48px rgba(0,0,0,0.5)' }}>
                    {/* Header */}
                    <div className="px-6 py-5 border-b" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
                      <h3 className="font-heading font-bold text-white text-lg mb-0.5">Verify your identity</h3>
                      <p className="text-sm" style={{ color: 'rgba(148,163,184,0.7)' }}>How would you like to receive your verification code?</p>
                    </div>
                    {/* Options */}
                    <div className="p-4 space-y-3">
                      {/* SMS option */}
                      <button onClick={() => { setVerifyMethod('sms'); sendOtpWithMethod('sms'); }}
                        disabled={loading}
                        className="w-full flex items-center gap-4 p-4 rounded-xl text-left transition-all hover:scale-[1.01] active:scale-[0.99]"
                        style={{ background: 'rgba(37,99,235,0.12)', border: '1px solid rgba(37,99,235,0.3)' }}>
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ background: 'rgba(37,99,235,0.2)' }}>
                          <Smartphone size={18} className="text-blue-400" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-white">Text message (SMS)</p>
                          <p className="text-xs mt-0.5" style={{ color: 'rgba(148,163,184,0.6)' }}>
                            Code sent to {countryCode} {form.phone}
                          </p>
                        </div>
                        {loading && verifyMethod === 'sms' && <Loader2 size={14} className="animate-spin text-blue-400 ml-auto" />}
                      </button>

                      {/* Email option */}
                      <button onClick={() => { setVerifyMethod('email'); sendOtpWithMethod('email'); }}
                        disabled={loading}
                        className="w-full flex items-center gap-4 p-4 rounded-xl text-left transition-all hover:scale-[1.01] active:scale-[0.99]"
                        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}>
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ background: 'rgba(255,255,255,0.08)' }}>
                          <Mail size={18} style={{ color: 'rgba(148,163,184,0.8)' }} />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-white">Email</p>
                          <p className="text-xs mt-0.5" style={{ color: 'rgba(148,163,184,0.6)' }}>
                            Code sent to {form.username}
                          </p>
                        </div>
                        {loading && verifyMethod === 'email' && <Loader2 size={14} className="animate-spin text-slate-400 ml-auto" />}
                      </button>
                    </div>
                    {/* Cancel */}
                    <div className="px-4 pb-4">
                      <button onClick={() => setShowMethodPicker(false)}
                        className="w-full text-xs py-2 text-center transition-colors"
                        style={{ color: 'rgba(100,116,139,0.5)' }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── STEP 2: OTP — 6 individual boxes ── */}
          {step === 2 && (
            <>
              <button onClick={() => setStep(1)} className="flex items-center gap-1.5 text-slate-500 hover:text-white text-sm mb-6 transition-colors">
                <ArrowLeft size={14} /> Back
              </button>
              <div className="flex items-center justify-center w-14 h-14 rounded-2xl mx-auto mb-4"
                style={{ background: 'rgba(37,99,235,0.15)', border: '1px solid rgba(37,99,235,0.3)' }}>
                <Smartphone size={26} className="text-blue-400" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-white text-center mb-1">
                Check your {otpDelivery === 'email' ? 'email' : 'phone'}
              </h2>
              <p className="text-sm text-center mb-1" style={{ color: 'rgba(148,163,184,0.7)' }}>
                {otpDelivery === 'email' ? 'Code emailed to' : 'We texted a code to'}
              </p>
              <p className="text-base font-semibold text-white text-center mb-2">
                {otpDelivery === 'email' ? form.username : `${countryCode} ${form.phone}`}
              </p>
              <p className="text-xs text-center mb-6" style={{ color: 'rgba(251,191,36,0.8)' }}>
                ⚠️ Check your spam folder if you don't see it
              </p>

              <form onSubmit={handleVerifyOtp} className="space-y-5">
                {/* 6 individual boxes */}
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-center mb-3"
                    style={{ color: 'rgba(148,163,184,0.5)' }}>Enter 6-digit code</p>
                  <div className="flex gap-2.5 justify-center">
                    {[0,1,2,3,4,5].map(idx => (
                      <input
                        key={idx}
                        id={`otp-${idx}`}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={otpCode[idx] || ''}
                        autoFocus={idx === 0}
                        style={{
                          width: 48, height: 58, textAlign: 'center',
                          fontSize: 24, fontWeight: 700,
                          fontFamily: 'Space Grotesk, sans-serif',
                          borderRadius: 12, outline: 'none',
                          background: otpCode[idx] ? 'rgba(37,99,235,0.22)' : 'rgba(255,255,255,0.06)',
                          border: `2px solid ${otpCode[idx] ? '#2563EB' : 'rgba(255,255,255,0.14)'}`,
                          color: 'white', caretColor: '#2563EB',
                          transition: 'border-color 0.15s, background 0.15s',
                        }}
                        onChange={e => {
                          const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 1);
                          // Build new code string
                          const chars = (otpCode + '      ').slice(0, 6).split('');
                          chars[idx] = val;
                          const newCode = chars.join('').replace(/ /g, '').padEnd(6, '').slice(0, 6);
                          // Actually just rebuild properly
                          const codeArr = Array(6).fill('');
                          for (let i = 0; i < Math.min(otpCode.length, 6); i++) codeArr[i] = otpCode[i];
                          codeArr[idx] = val;
                          const joined = codeArr.join('');
                          setOtpCode(joined);
                          setOtpError('');
                          if (val && idx < 5) document.getElementById(`otp-${idx + 1}`)?.focus();
                          // Auto-submit with fresh value — no stale closure
                          const filledCount = codeArr.filter(c => c !== '').length;
                          if (filledCount === 6) {
                            setTimeout(() => verifyCode(joined), 150);
                          }
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Backspace' && !otpCode[idx] && idx > 0) {
                            const codeArr = Array(6).fill('');
                            for (let i = 0; i < Math.min(otpCode.length, 6); i++) codeArr[i] = otpCode[i];
                            codeArr[idx - 1] = '';
                            setOtpCode(codeArr.join(''));
                            document.getElementById(`otp-${idx - 1}`)?.focus();
                          }
                        }}
                        onPaste={e => {
                          e.preventDefault();
                          const pasted = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, 6);
                          setOtpCode(pasted);
                          setOtpError('');
                          const nextIdx = Math.min(pasted.length, 5);
                          document.getElementById(`otp-${nextIdx}`)?.focus();
                          if (pasted.length === 6) setTimeout(() => verifyCode(pasted), 150);
                        }}
                      />
                    ))}
                  </div>
                  {otpError && <p className="text-xs text-red-400 text-center mt-3 font-medium">{otpError}</p>}
                  <p className="text-xs text-center mt-2" style={{ color: 'rgba(100,116,139,0.45)' }}>
                    Auto-submits when complete · Expires in 10 min
                  </p>
                </div>

                <Button type="submit" className="w-full h-11 gap-2 font-semibold"
                  disabled={loading || otpCode.replace(/\s/g,'').length < 6}>
                  {loading ? <><Loader2 size={15} className="animate-spin" /> Verifying…</> : <><ArrowRight size={15} /> Verify & Go to Payment</>}
                </Button>

                <div className="text-center space-y-1">
                  <p className="text-xs" style={{ color: 'rgba(100,116,139,0.5)' }}>Didn't get a code?</p>
                  <button type="button" onClick={resendOtp} disabled={otpResending}
                    className="text-xs text-blue-400 hover:text-blue-300 font-medium transition-colors">
                    {otpResending ? <><Loader2 size={11} className="inline animate-spin mr-1" />Sending…</> : '↺ Send a new code'}
                  </button>
                </div>
              </form>
            </>
          )}

          {/* ── STEP 2: Redirecting to Payment ── */}
          {step === 2 && (
            <div className="text-center py-8">
              <Loader2 size={40} className="text-blue-400 animate-spin mx-auto mb-5" />
              <h2 className="font-heading text-xl font-bold text-white mb-2">Redirecting to checkout...</h2>
              <p className="text-slate-400 text-sm">$249/month · Cancel anytime</p>
            </div>
          )}

          {step === 1 && (
            <p className="text-center mt-6 text-sm" style={{ color: 'rgba(148,163,184,0.5)' }}>
              Already have an account?{' '}
              <Link to="/login" className="text-blue-400 font-semibold hover:text-blue-300">Sign in</Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
